/**
 * App.jsx
 * 
 * Main application component with React Router.
 * Handles routing between pages while preserving the existing UI structure.
 */

import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import AIToolExplorer from './AIToolExplorer';
import BlogPostPage from './components/BlogPostPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import ContactPage from './components/ContactPage';
import CookiePolicyPage from './components/CookiePolicyPage';
import EditorialPolicyPage from './components/EditorialPolicyPage';
import MachineReadableResourcesPage from './components/MachineReadableResourcesPage';
import SEOHead from './components/SEOHead';
import { buildContactPageNodes } from './utils/schema';
import { CANONICAL_URL } from './data/siteConfig.js';

export default function App() {
  const location = useLocation();
  
  // Pages that manage their own SEO head
  const hasOwnSEO = location.pathname === '/home' ||
    location.pathname === '/' ||
    location.pathname.startsWith('/blog') ||
    location.pathname.startsWith('/database');
  const pageMeta = getPageMeta(location.pathname);
  
  return (
    <>
      {/* Default SEO for pages that don't set their own */}
      {!hasOwnSEO && (
        <SEOHead
          title={pageMeta.title}
          description={pageMeta.description}
          path={location.pathname}
          dateModified={pageMeta.dateModified}
          breadcrumbs={pageMeta.breadcrumbs}
          noindex={pageMeta.noindex}
          extraGraph={pageMeta.extraGraph}
        />
      )}
      
      <Routes>
        {/* Blog post route - individual articles (has its own header) */}
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        
        {/* Legal & policy pages */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/editorial-policy" element={<EditorialPolicyPage />} />
        <Route path="/resources" element={<MachineReadableResourcesPage />} />
        
        {/* All routes (including /onboarding, /home, /database/:slug) handled by AIToolExplorer — one shared header */}
        <Route path="/*" element={<AIToolExplorer />} />
      </Routes>
    </>
  );
}

const DEFAULT_DESCRIPTION = 'Neutral research platform documenting real-world AI tool usage. Compare AI tools based on your context, not rankings.';
const DEFAULT_DATE_MODIFIED = '2026-03-23';

function getPageMeta(pathname) {
  switch (pathname) {
    case '/':
      return {
        title: 'AI Tool Globe Explorer',
        description: 'Interactive globe for exploring AI tools and categories inside Touchstone. For extractable research content, use the homepage, database, and blog.',
        dateModified: DEFAULT_DATE_MODIFIED,
        noindex: true,
      };
    case '/onboarding':
      return {
        title: 'Get Started',
        description: 'Set up your profile and get personalized AI tool recommendations based on your role, budget, and requirements.',
        dateModified: DEFAULT_DATE_MODIFIED,
        breadcrumbs: [
          { name: 'Home', url: `${CANONICAL_URL}/home` },
          { name: 'Onboarding', url: `${CANONICAL_URL}/onboarding` },
        ],
        noindex: true,
      };
    case '/home':
      return { title: 'Touchstone — Neutral AI Tool Research Platform', description: DEFAULT_DESCRIPTION, dateModified: DEFAULT_DATE_MODIFIED };
    case '/blog':
      return {
        title: 'Blog',
        description: 'Factual articles documenting AI tool capabilities, limitations, and context-driven fit. No rankings, no affiliate links.',
        dateModified: DEFAULT_DATE_MODIFIED,
      };
    case '/workflow':
      return {
        title: 'Workflow Control',
        description: 'Build and manage your personalized AI tool workflow. Identify overlaps, gaps, and integration opportunities in your stack.',
        dateModified: DEFAULT_DATE_MODIFIED,
        breadcrumbs: [
          { name: 'Home', url: `${CANONICAL_URL}/home` },
          { name: 'Workflow', url: `${CANONICAL_URL}/workflow` },
        ],
        noindex: true,
      };
    case '/database':
      return { title: 'Tool Database', description: DEFAULT_DESCRIPTION, dateModified: DEFAULT_DATE_MODIFIED };
    case '/privacy':
      return {
        title: 'Privacy Policy',
        description: 'How Touchstone collects, uses, and protects your personal information.',
        dateModified: '2026-02-16',
        breadcrumbs: [
          { name: 'Home', url: `${CANONICAL_URL}/home` },
          { name: 'Privacy Policy', url: `${CANONICAL_URL}/privacy` },
        ],
      };
    case '/terms':
      return {
        title: 'Terms of Service',
        description: 'Terms and conditions for using the Touchstone platform.',
        dateModified: '2026-02-16',
        breadcrumbs: [
          { name: 'Home', url: `${CANONICAL_URL}/home` },
          { name: 'Terms of Service', url: `${CANONICAL_URL}/terms` },
        ],
      };
    case '/contact':
      return {
        title: 'Contact Us',
        description: 'Get in touch with the Touchstone team for questions, feedback, corrections, or partnership inquiries.',
        dateModified: '2026-02-17',
        breadcrumbs: [
          { name: 'Home', url: `${CANONICAL_URL}/home` },
          { name: 'Contact', url: `${CANONICAL_URL}/contact` },
        ],
        extraGraph: buildContactPageNodes(),
      };
    case '/cookies':
      return {
        title: 'Cookie Policy',
        description: 'How Touchstone uses cookies and similar technologies on the platform.',
        dateModified: '2026-02-16',
        breadcrumbs: [
          { name: 'Home', url: `${CANONICAL_URL}/home` },
          { name: 'Cookie Policy', url: `${CANONICAL_URL}/cookies` },
        ],
      };
    case '/editorial-policy':
      return {
        title: 'Editorial Policy',
        description: 'How Touchstone maintains neutrality, evidence standards, correction policy, and independence in tool evaluations.',
        dateModified: '2026-02-17',
        breadcrumbs: [
          { name: 'Home', url: `${CANONICAL_URL}/home` },
          { name: 'Editorial Policy', url: `${CANONICAL_URL}/editorial-policy` },
        ],
      };
    case '/resources':
      return {
        title: 'Machine-Readable Resources',
        description: 'Machine-readable and trust resources for Touchstone, including sitemap, llms files, editorial policy, contact details, and update methodology.',
        dateModified: DEFAULT_DATE_MODIFIED,
        breadcrumbs: [
          { name: 'Home', url: `${CANONICAL_URL}/home` },
          { name: 'Machine-Readable Resources', url: `${CANONICAL_URL}/resources` },
        ],
        extraGraph: [
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            '@id': `${CANONICAL_URL}/resources#webpage`,
            url: `${CANONICAL_URL}/resources`,
            name: 'Touchstone Machine-Readable Resources',
            description: 'Index of machine-readable and trust resources published by Touchstone.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, url: `${CANONICAL_URL}/sitemap.xml`, name: 'Sitemap XML' },
              { '@type': 'ListItem', position: 2, url: `${CANONICAL_URL}/llms.txt`, name: 'LLMs TXT' },
              { '@type': 'ListItem', position: 3, url: `${CANONICAL_URL}/llms-full.txt`, name: 'LLMs Full TXT' },
              { '@type': 'ListItem', position: 4, url: `${CANONICAL_URL}/editorial-policy`, name: 'Editorial Policy' },
              { '@type': 'ListItem', position: 5, url: `${CANONICAL_URL}/contact`, name: 'Contact' },
            ],
          },
        ],
      };
    default:
      return { title: null, description: DEFAULT_DESCRIPTION, dateModified: DEFAULT_DATE_MODIFIED, noindex: false, extraGraph: [] };
  }
}
